import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Inbox, Send, MessageSquare, Plus, Search, FileText, Download, Eye, 
  User, Calendar, Clock, X, Paperclip, Check, Users, Mail, Trash2,
  Reply, MessageCircle, CheckCircle2, XCircle, Package, Loader2
} from 'lucide-react';

const DepotCommunication = ({ branchIdOverride, readOnly = false }) => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [inboxMessages, setInboxMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Modal states
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showMessageDetailModal, setShowMessageDetailModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  
  // Reply form state
  const [replyContent, setReplyContent] = useState('');
  const [replyDocuments, setReplyDocuments] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  
  // Delivery confirmation state
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);
  
  // New message form
  const [usersForMessage, setUsersForMessage] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageDescription, setMessageDescription] = useState('');
  const [messageDocuments, setMessageDocuments] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [currentUserName, setCurrentUserName] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Get current user info
  useEffect(() => {
    const token = localStorage.getItem('fortis_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.sub || '');
        // Get user name from localStorage or token
        const userStr = localStorage.getItem('fortis_user');
        if (userStr) {
          const user = JSON.parse(userStr);
          setCurrentUserName(user.name || '');
          setCurrentUserId(user.id || payload.sub || '');
        }
      } catch (e) {
        console.error('Token parse hatası:', e);
      }
    }
  }, []);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Fetch inbox
      const inboxResponse = await fetch(`${API_URL}/api/work/messages/inbox`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (inboxResponse.ok) {
        const data = await inboxResponse.json();
        setInboxMessages(data);
      }
      
      // Fetch sent
      const sentResponse = await fetch(`${API_URL}/api/work/messages/sent`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (sentResponse.ok) {
        const data = await sentResponse.json();
        setSentMessages(data);
      }
      
      // Fetch unread count
      const unreadResponse = await fetch(`${API_URL}/api/work/messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (unreadResponse.ok) {
        const data = await unreadResponse.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Mesajlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Fetch users for message
  const fetchUsersForMessage = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/work/users-for-message`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsersForMessage(data.users || []);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Open new message modal
  const openNewMessageModal = () => {
    setSelectedRecipients([]);
    setMessageTitle('');
    setMessageDescription('');
    setMessageDocuments([]);
    setUserSearchTerm('');
    fetchUsersForMessage();
    setShowNewMessageModal(true);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setMessageDocuments(prev => [...prev, {
          name: file.name,
          data: event.target.result,
          type: file.type,
          size: file.size
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove document
  const removeDocument = (index) => {
    setMessageDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // Toggle recipient selection
  const toggleRecipient = (userId) => {
    setSelectedRecipients(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Select all users
  const selectAllUsers = () => {
    const filtered = filteredUsers.map(u => u.id);
    setSelectedRecipients(filtered);
  };

  // Deselect all users
  const deselectAllUsers = () => {
    setSelectedRecipients([]);
  };

  // Send message
  const handleSendMessage = async () => {
    if (!messageTitle.trim()) {
      toast.error('Başlık gerekli');
      return;
    }
    if (!messageDescription.trim()) {
      toast.error('Açıklama gerekli');
      return;
    }
    if (selectedRecipients.length === 0) {
      toast.error('En az bir alıcı seçmelisiniz');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/work/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: messageTitle,
          description: messageDescription,
          recipient_ids: selectedRecipients,
          documents: messageDocuments
        })
      });
      
      if (response.ok) {
        toast.success('Mesaj başarıyla gönderildi');
        setShowNewMessageModal(false);
        fetchMessages();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Mesaj gönderilemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // View message detail
  const handleViewMessage = async (message, isInbox = true) => {
    setSelectedMessage(message);
    setShowMessageDetailModal(true);
    setShowReplyForm(false);
    setReplyContent('');
    setReplyDocuments([]);
    
    // Mark as read if inbox message and not read
    if (isInbox && !message.is_read) {
      try {
        const token = localStorage.getItem('fortis_token');
        await fetch(`${API_URL}/api/work/messages/${message.id}/mark-read`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Update local state
        setInboxMessages(prev => prev.map(m => 
          m.id === message.id ? { ...m, is_read: true } : m
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Okundu işaretleme hatası:', error);
      }
    }
  };

  // Handle reply document upload
  const handleReplyDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setReplyDocuments(prev => [...prev, {
          name: file.name,
          data: event.target.result,
          type: file.type,
          size: file.size
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove reply document
  const removeReplyDocument = (index) => {
    setReplyDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // Send reply
  const handleSendReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Cevap içeriği gerekli');
      return;
    }
    
    setSendingReply(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/work/messages/${selectedMessage.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: replyContent,
          documents: replyDocuments
        })
      });
      
      if (response.ok) {
        toast.success('Cevap gönderildi');
        setReplyContent('');
        setReplyDocuments([]);
        setShowReplyForm(false);
        
        // Refresh message to get updated thread
        const threadResponse = await fetch(`${API_URL}/api/work/messages/${selectedMessage.id}/thread`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (threadResponse.ok) {
          const updatedMessage = await threadResponse.json();
          setSelectedMessage(updatedMessage);
        }
        
        // Refresh messages list
        fetchMessages();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Cevap gönderilemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setSendingReply(false);
    }
  };

  // Handle delivery confirmation (Kabul Et / Reddet)
  const handleDeliveryConfirmation = async (action) => {
    if (!selectedMessage?.delivery_data?.stok_cikisi_id) {
      toast.error('Stok çıkışı bilgisi bulunamadı');
      return;
    }
    
    const confirmText = action === 'confirm' 
      ? 'Teslimatı onaylamak istediğinizden emin misiniz?'
      : 'Teslimatı reddetmek istediğinizden emin misiniz?';
    
    if (!window.confirm(confirmText)) {
      return;
    }
    
    setConfirmingDelivery(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(
        `${API_URL}/api/depot/stok-cikisleri/${selectedMessage.delivery_data.stok_cikisi_id}/confirm-delivery`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ action })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || (action === 'confirm' ? 'Teslimat onaylandı' : 'Teslimat reddedildi'));
        
        // Update local message state
        setSelectedMessage(prev => ({
          ...prev,
          delivery_data: {
            ...prev.delivery_data,
            delivery_status: action === 'confirm' ? 'confirmed' : 'rejected'
          }
        }));
        
        // Refresh messages list
        fetchMessages();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Teslimat onay hatası:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setConfirmingDelivery(false);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Bu mesajı silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API_URL}/api/work/messages/${messageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Mesaj silindi');
        setShowMessageDetailModal(false);
        fetchMessages();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Mesaj silinemedi');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Filter users by search
  const filteredUsers = usersForMessage.filter(user =>
    user.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.position?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  // Get selected user names
  const getSelectedUserNames = () => {
    return selectedRecipients
      .map(id => usersForMessage.find(u => u.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  // Message list component
  const MessageList = ({ messages, isInbox, emptyMessage }) => (
    <ScrollArea className="h-[500px]">
      {messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          {isInbox ? <Inbox className="h-16 w-16 mb-4 text-gray-300" /> : <Send className="h-16 w-16 mb-4 text-gray-300" />}
          <p className="font-medium">{emptyMessage}</p>
        </div>
      ) : (
        <div className="space-y-3 pr-4">
          {messages.map((message) => (
            <div
              key={message.id}
              onClick={() => handleViewMessage(message, isInbox)}
              className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                isInbox && !message.is_read 
                  ? 'bg-blue-50 border-blue-300 border-l-4 border-l-blue-500' 
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`message-item-${message.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {isInbox && !message.is_read && (
                      <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse flex-shrink-0" />
                    )}
                    {/* Teslimat Onayı Badge */}
                    {message.message_type === 'delivery_confirmation' && (
                      <>
                        <Package className="h-4 w-4 text-amber-600 flex-shrink-0" />
                        {message.delivery_data?.delivery_status === 'pending' && (
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs px-1.5 py-0">
                            Onay Bekliyor
                          </Badge>
                        )}
                        {message.delivery_data?.delivery_status === 'confirmed' && (
                          <Badge className="bg-green-100 text-green-800 border-green-300 text-xs px-1.5 py-0">
                            Teslim Edildi
                          </Badge>
                        )}
                        {message.delivery_data?.delivery_status === 'rejected' && (
                          <Badge className="bg-red-100 text-red-800 border-red-300 text-xs px-1.5 py-0">
                            Reddedildi
                          </Badge>
                        )}
                      </>
                    )}
                    <h4 className={`truncate ${isInbox && !message.is_read ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>
                      {message.title}
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">{message.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {isInbox ? message.sender_name : message.recipient_names?.join(', ')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(message.created_at)}
                    </span>
                    {message.documents && message.documents.length > 0 && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <Paperclip className="h-3 w-3" />
                        {message.documents.length} belge
                      </span>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="flex-shrink-0">
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ScrollArea>
  );

  return (
    <div className="space-y-6" data-testid="work-communication">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">İLETİŞİM</h1>
          <p className="text-gray-500">Mesajlaşma ve iletişim merkezi</p>
        </div>
        <Button onClick={openNewMessageModal} className="bg-blue-600 hover:bg-blue-700" data-testid="new-message-btn">
          <Plus className="h-4 w-4 mr-2" />
          Mesaj Gönder
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="inbox" className="flex items-center gap-2" data-testid="inbox-tab">
            <Inbox className="h-4 w-4" />
            Gelen Kutusu
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] px-1 text-xs animate-pulse">
                {unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sent" className="flex items-center gap-2" data-testid="sent-tab">
            <Send className="h-4 w-4" />
            Gönderilen
            {sentMessages.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1 text-xs">
                {sentMessages.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Inbox className="h-5 w-5 text-blue-600" />
                Gelen Mesajlar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <MessageList 
                messages={inboxMessages} 
                isInbox={true}
                emptyMessage="Henüz gelen mesaj yok"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sent">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Send className="h-5 w-5 text-green-600" />
                Gönderilen Mesajlar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <MessageList 
                messages={sentMessages} 
                isInbox={false}
                emptyMessage="Henüz mesaj göndermediniz"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Message Modal */}
      <Dialog open={showNewMessageModal} onOpenChange={setShowNewMessageModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Yeni Mesaj
            </DialogTitle>
            <DialogDescription>
              Şubenizdeki kişilere mesaj gönderin
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Sender Info (Auto-filled) */}
            <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-xs text-gray-500">Gönderen</Label>
                <p className="font-medium text-gray-900">{currentUserName || 'Kullanıcı'}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Tarih / Saat</Label>
                <p className="font-medium text-gray-900">{formatDate(new Date().toISOString())}</p>
              </div>
            </div>

            {/* Recipients Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Alıcılar *
                </Label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAllUsers}>
                    Tümünü Seç
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={deselectAllUsers}>
                    Temizle
                  </Button>
                </div>
              </div>
              
              {selectedRecipients.length > 0 && (
                <div className="p-2 bg-blue-50 rounded text-sm text-blue-700">
                  <strong>Seçilen ({selectedRecipients.length}):</strong> {getSelectedUserNames()}
                </div>
              )}
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="İsim veya görev ile ara..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {loadingUsers ? (
                  <div className="p-4 text-center text-gray-500">Yükleniyor...</div>
                ) : filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">Kullanıcı bulunamadı</div>
                ) : (
                  <div className="divide-y">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 ${
                          selectedRecipients.includes(user.id) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => toggleRecipient(user.id)}
                      >
                        <Checkbox 
                          checked={selectedRecipients.includes(user.id)}
                          onCheckedChange={() => toggleRecipient(user.id)}
                        />
                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold text-sm">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{user.name}</p>
                          {user.position && <p className="text-xs text-gray-500">{user.position}</p>}
                        </div>
                        {selectedRecipients.includes(user.id) && (
                          <Check className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="message-title">Başlık *</Label>
              <Input
                id="message-title"
                value={messageTitle}
                onChange={(e) => setMessageTitle(e.target.value)}
                placeholder="Mesaj başlığı yazın..."
                data-testid="message-title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="message-description">Açıklama *</Label>
              <Textarea
                id="message-description"
                value={messageDescription}
                onChange={(e) => setMessageDescription(e.target.value)}
                placeholder="Mesaj içeriğini yazın..."
                rows={5}
                data-testid="message-description-input"
              />
            </div>

            {/* Document Upload */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Belgeler
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="flex-1"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                />
              </div>
              
              {messageDocuments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {messageDocuments.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm">{doc.name}</span>
                        <span className="text-xs text-gray-400">
                          ({(doc.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeDocument(index)}
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewMessageModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSendMessage} 
              disabled={loading || !messageTitle.trim() || !messageDescription.trim() || selectedRecipients.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-message-btn"
            >
              {loading ? 'Gönderiliyor...' : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Gönder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Detail Modal */}
      <Dialog open={showMessageDetailModal} onOpenChange={setShowMessageDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {selectedMessage?.title}
            </DialogTitle>
          </DialogHeader>

          {selectedMessage && (
            <div className="space-y-4 py-4">
              {/* Message Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Gönderen</Label>
                  <p className="font-medium">{selectedMessage.sender_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Tarih</Label>
                  <p className="font-medium">{formatDate(selectedMessage.created_at)}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-gray-500">Alıcılar</Label>
                  <p className="font-medium">{selectedMessage.recipient_names?.join(', ')}</p>
                </div>
              </div>

              {/* Delivery Confirmation Badge */}
              {selectedMessage.message_type === 'delivery_confirmation' && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-5 w-5 text-amber-600" />
                    <span className="font-semibold text-amber-800">Malzeme Teslim Tutanağı</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Teslimat Durumu:</span>
                    {selectedMessage.delivery_data?.delivery_status === 'pending' && (
                      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                        ⏳ Onay Bekliyor
                      </Badge>
                    )}
                    {selectedMessage.delivery_data?.delivery_status === 'confirmed' && (
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        ✅ Teslim Edildi
                      </Badge>
                    )}
                    {selectedMessage.delivery_data?.delivery_status === 'rejected' && (
                      <Badge className="bg-red-100 text-red-800 border-red-300">
                        ❌ Teslim Reddedildi
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Original Message Content */}
              <div className="space-y-2">
                <Label className="text-xs text-gray-500">Mesaj</Label>
                <div className="p-4 bg-white border rounded-lg whitespace-pre-wrap">
                  {selectedMessage.description}
                </div>
              </div>

              {/* Original Message Documents */}
              {selectedMessage.documents && selectedMessage.documents.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500 flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Belgeler ({selectedMessage.documents.length})
                  </Label>
                  <div className="space-y-2">
                    {selectedMessage.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-gray-500">
                              {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : 'Belge'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const newWindow = window.open();
                              if (newWindow && doc.data) {
                                if (doc.data.startsWith('data:image/')) {
                                  newWindow.document.write(`<html><head><title>Belge Önizleme</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f0f0f0;"><img src="${doc.data}" style="max-width:100%;max-height:100vh;object-fit:contain;" /></body></html>`);
                                } else if (doc.data.startsWith('data:application/pdf')) {
                                  newWindow.document.write(`<html><head><title>PDF Önizleme</title></head><body style="margin:0;"><iframe src="${doc.data}" style="width:100%;height:100vh;border:none;"></iframe></body></html>`);
                                } else {
                                  newWindow.location.href = doc.data;
                                }
                              }
                            }}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Görüntüle
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              if (doc.data) {
                                const link = document.createElement('a');
                                link.href = doc.data;
                                link.download = doc.name || 'belge';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            İndir
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Replies Thread */}
              {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                <div className="space-y-3 pt-4 border-t">
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Cevaplar ({selectedMessage.replies.length})
                  </Label>
                  <div className="space-y-3">
                    {selectedMessage.replies.map((reply, index) => (
                      <div 
                        key={reply.id || index} 
                        className={`p-4 rounded-lg border ${
                          reply.sender_id === currentUserId 
                            ? 'bg-blue-50 border-blue-200 ml-8' 
                            : 'bg-gray-50 border-gray-200 mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm flex items-center gap-2">
                            <User className="h-4 w-4" />
                            {reply.sender_name}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(reply.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{reply.content}</p>
                        
                        {/* Reply Documents */}
                        {reply.documents && reply.documents.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {reply.documents.map((doc, docIndex) => (
                              <div key={docIndex} className="flex items-center justify-between p-2 bg-white border rounded">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-blue-600" />
                                  <span className="text-sm">{doc.name}</span>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      const newWindow = window.open();
                                      if (newWindow && doc.data) {
                                        if (doc.data.startsWith('data:image/')) {
                                          newWindow.document.write(`<html><head><title>Belge Önizleme</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f0f0f0;"><img src="${doc.data}" style="max-width:100%;max-height:100vh;object-fit:contain;" /></body></html>`);
                                        } else if (doc.data.startsWith('data:application/pdf')) {
                                          newWindow.document.write(`<html><head><title>PDF Önizleme</title></head><body style="margin:0;"><iframe src="${doc.data}" style="width:100%;height:100vh;border:none;"></iframe></body></html>`);
                                        } else {
                                          newWindow.location.href = doc.data;
                                        }
                                      }
                                    }}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      if (doc.data) {
                                        const link = document.createElement('a');
                                        link.href = doc.data;
                                        link.download = doc.name || 'belge';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }
                                    }}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delivery Confirmation Buttons */}
              {selectedMessage.message_type === 'delivery_confirmation' && 
               selectedMessage.delivery_data?.delivery_status === 'pending' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-gray-700 mb-3">
                    Bu teslimatı onaylıyor veya reddediyor musunuz?
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleDeliveryConfirmation('confirm')}
                      disabled={confirmingDelivery}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="confirm-delivery-btn"
                    >
                      {confirmingDelivery ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      Kabul Et
                    </Button>
                    <Button
                      onClick={() => handleDeliveryConfirmation('reject')}
                      disabled={confirmingDelivery}
                      variant="destructive"
                      className="flex-1"
                      data-testid="reject-delivery-btn"
                    >
                      {confirmingDelivery ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      Reddet
                    </Button>
                  </div>
                </div>
              )}

              {/* Reply Form */}
              {showReplyForm ? (
                <div className="space-y-3 pt-4 border-t bg-gray-50 p-4 rounded-lg">
                  <Label className="font-semibold flex items-center gap-2">
                    <Reply className="h-4 w-4" />
                    Cevap Yaz
                  </Label>
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Cevabınızı yazın..."
                    rows={4}
                    className="bg-white"
                    data-testid="reply-content-input"
                  />
                  
                  {/* Reply Document Upload */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-2">
                      <Paperclip className="h-4 w-4" />
                      Belge Ekle
                    </Label>
                    <Input
                      type="file"
                      multiple
                      onChange={handleReplyDocUpload}
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      className="bg-white"
                    />
                    {replyDocuments.length > 0 && (
                      <div className="space-y-1">
                        {replyDocuments.map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-blue-600" />
                              <span className="text-sm">{doc.name}</span>
                              <span className="text-xs text-gray-400">
                                ({(doc.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm"
                              onClick={() => removeReplyDocument(index)}
                            >
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 justify-end">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowReplyForm(false);
                        setReplyContent('');
                        setReplyDocuments([]);
                      }}
                    >
                      İptal
                    </Button>
                    <Button 
                      onClick={handleSendReply}
                      disabled={sendingReply || !replyContent.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="send-reply-btn"
                    >
                      {sendingReply ? 'Gönderiliyor...' : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Cevap Gönder
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="pt-4 border-t">
                  <Button 
                    onClick={() => setShowReplyForm(true)}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="reply-btn"
                  >
                    <Reply className="h-4 w-4 mr-2" />
                    Cevapla
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedMessage?.sender_id === currentUserId && (
              <Button 
                variant="destructive" 
                onClick={() => handleDeleteMessage(selectedMessage.id)}
                className="mr-auto"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Sil
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowMessageDetailModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepotCommunication;
